// LeadManagementPanel.jsx — V34 BookMyTutor-style flat-table CRM view for the
// Admin Lead Pipeline. Replaces the old kanban look with a familiar lead-list
// layout: KPI strip → filter bar → table rows → row Actions popover. Powered
// by the existing /api/admin/pipeline/* endpoints; no backend changes needed.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Users as UsersIcon,
    UserPlus,
    Target,
    TrendingUp,
    RefreshCw,
    Plus,
    Search,
    MoreHorizontal,
    Eye,
    EyeOff,
    Copy as CopyIcon,
    Trash2,
    FileText,
    MapPin,
    Phone as PhoneIcon,
    Loader2,
    ShieldAlert,
    CheckCircle2,
    GraduationCap,
    RotateCcw,
    Activity,
    Pencil,
    XCircle,
    Undo2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSuperMode } from "../../context/SuperModeContext";
import CreateDemoLeadModal from "./CreateDemoLeadModal";
import LeadDetailModal from "./LeadDetailModal";

// Pipeline stages exposed to the operator as sidebar-style tabs (mirrors
// BookMyTutor's Leads / Posting / Demos / Tuitions sections).
const STAGE_TABS = [
    { id: "leads", label: "Demo Leads" },
    { id: "demo", label: "Demo Posting" },
    { id: "demo_status", label: "Demo Tutor" },
    { id: "completed_demo", label: "Demo Done" },
    { id: "ongoing_tuition", label: "Ongoing Tuitions" },
    { id: "closed", label: "Closed" },
];

const PRIORITY_TONE = {
    high: "bg-rose-100 text-rose-700 ring-rose-200",
    normal: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    low: "bg-slate-100 text-slate-600 ring-slate-200",
};

const SOURCE_TONE = {
    website: "bg-violet-50 text-violet-700 ring-violet-200",
    call: "bg-sky-50 text-sky-700 ring-sky-200",
    walkin: "bg-amber-50 text-amber-700 ring-amber-200",
    manual: "bg-slate-100 text-slate-700 ring-slate-200",
    referral: "bg-pink-50 text-pink-700 ring-pink-200",
};

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yy = String(d.getFullYear()).slice(-2);
        let h = d.getHours();
        const min = String(d.getMinutes()).padStart(2, "0");
        const ampm = h >= 12 ? "PM" : "AM";
        h = h % 12 || 12;
        return `${dd}-${mm}-${yy}, ${String(h).padStart(2, "0")}:${min} ${ampm}`;
    } catch { return iso; }
}

function pctSafe(num, den) {
    if (!den) return "0 %";
    return `${Math.round((num / den) * 100)} %`;
}

export default function LeadManagementPanel() {
    const sm = useSuperMode();
    const [stage, setStage] = useState("leads");
    const [counts, setCounts] = useState({});
    const [newLeads24h, setNewLeads24h] = useState(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [source, setSource] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [createSeed, setCreateSeed] = useState(null);
    const [detail, setDetail] = useState(null);
    const [openMenu, setOpenMenu] = useState(null);
    const [confirming, setConfirming] = useState(null); // {id, name}

    const loadCounts = useCallback(async () => {
        if (!sm?.active) return;
        try {
            const r = await api.get("/admin/pipeline/counts");
            setCounts(r.data?.counts || {});
        } catch {
            // soft-fail; KPIs render as 0
        }
        try {
            // "New Leads" = leads stage created in the last 24h
            const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
            const r2 = await api.get(`/admin/pipeline/registrations?stage=leads&date_from=${encodeURIComponent(since)}&limit=1000`);
            setNewLeads24h(r2.data?.count ?? (r2.data?.items?.length || 0));
        } catch {
            setNewLeads24h(0);
        }
    }, [sm?.active]);

    const load = useCallback(async () => {
        if (!sm?.active) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ stage, limit: "200", sort: "created_at_desc" });
            if (q) params.set("q", q);
            const r = await api.get(`/admin/pipeline/registrations?${params.toString()}`);
            let rows = r.data?.items || [];
            if (source) {
                if (stage === "demo") {
                    const min = parseInt(source, 10) || 0;
                    if (min === 0) {
                        rows = rows.filter((r1) => (r1.applicants_count || 0) === 0);
                    } else {
                        rows = rows.filter((r1) => (r1.applicants_count || 0) >= min);
                    }
                } else if (stage === "demo_status") {
                    rows = rows.filter((r1) => (r1.pipeline_sub_status || "").toLowerCase() === source.toLowerCase());
                } else if (stage === "ongoing_tuition") {
                    rows = rows.filter((r1) => (r1.fee_status || "").toLowerCase() === source.toLowerCase());
                } else {
                    rows = rows.filter((r1) => {
                        const src = (r1.source || r1.lead_source || r1.callback_meta?.cta_source || "").toLowerCase();
                        return src.includes(source.toLowerCase());
                    });
                }
            }
            setItems(rows);
        } catch {
            toast.error("Failed to load leads");
        } finally {
            setLoading(false);
        }
    }, [stage, q, source, sm?.active]);

    useEffect(() => { loadCounts(); }, [loadCounts]);
    useEffect(() => { load(); }, [load]);
    const refresh = () => { loadCounts(); load(); };

    const kpis = useMemo(() => {
        const open = counts.leads || 0;
        const inDemo = (counts.demo || 0) + (counts.demo_status || 0) + (counts.completed_demo || 0);
        const converted = counts.ongoing_tuition || 0;
        const allDemo = open + inDemo + converted + (counts.closed || 0);
        return [
            { label: "Open Leads", value: open, Icon: UsersIcon, accent: "from-blue-50 to-blue-100", iconTone: "bg-blue-500/10 text-blue-600", testid: "kpi-open-leads" },
            { label: "New Leads", value: newLeads24h, Icon: UserPlus, accent: "from-indigo-50 to-indigo-100", iconTone: "bg-indigo-500/10 text-indigo-600", testid: "kpi-new-leads" },
            { label: "In Demo Leads", value: inDemo, Icon: Target, accent: "from-violet-50 to-violet-100", iconTone: "bg-violet-500/10 text-violet-600", testid: "kpi-in-demo" },
            { label: "Converted", value: converted, Icon: TrendingUp, accent: "from-emerald-50 to-emerald-100", iconTone: "bg-emerald-500/10 text-emerald-600", testid: "kpi-converted" },
            { label: "Conversion Rate", value: pctSafe(converted, allDemo), Icon: Target, accent: "from-rose-50 to-rose-100", iconTone: "bg-rose-500/10 text-rose-600", testid: "kpi-conversion" },
        ];
    }, [counts, newLeads24h]);

    const handleDuplicate = (row) => {
        setOpenMenu(null);
        // Pre-seed the Create modal with the lead's payload (minus id).
        setCreateSeed({
            name: row.name || "",
            parent_name: row.parent_name || "",
            phone: row.phone || "",
            email: row.email || "",
            city: row.city || "",
            state: row.state || "",
            pin_code: row.pin_code || "",
            grade: row.grade || "",
            board: row.board || "",
            subjects: row.subjects || "",
            mode: row.mode || "",
            notes: row.notes || "",
        });
        setCreateOpen(true);
    };

    const handleDelete = async (row) => {
        setOpenMenu(null);
        try {
            await api.delete(`/admin/pipeline/registrations/${row.id}`);
            toast.success("Lead archived");
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to archive");
        } finally {
            setConfirming(null);
        }
    };

    const isPostings = stage === "demo";
    const isDemo = stage === "demo_status";
    const isTuition = stage === "ongoing_tuition";
    const isCompletedDemo = stage === "completed_demo";
    const isClosed = stage === "closed";

    const handleCallLead = (row) => {
        setOpenMenu(null);
        if (!row.phone) {
            toast.error("No parent phone on record");
            return;
        }
        // Open the device dialer / SIP client.
        window.location.href = `tel:${row.phone}`;
    };

    const handleStartTuition = async (row) => {
        setOpenMenu(null);
        try {
            await api.post(`/admin/pipeline/registrations/${row.id}/transition`, {
                to_stage: "ongoing_tuition",
                sub_status: "active",
                reason: "Started tuition from Demo Done",
            });
            toast.success("Tuition started — moved to Ongoing Tuitions");
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to start tuition");
        }
    };

    const handleCloseAsLost = async (row) => {
        setOpenMenu(null);
        try {
            await api.post(`/admin/pipeline/registrations/${row.id}/transition`, {
                to_stage: "closed",
                sub_status: "lost",
                reason: "Closed as lost from Demo Done",
            });
            toast.success("Lead closed as lost");
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to close lead");
        }
    };

    const handleReassignTutor = async (row) => {
        setOpenMenu(null);
        try {
            await api.post(`/admin/leads/${row.id}/cancel-assignment`, { reason: "Reassigning tutor from Demo Done" });
            toast.success("Assignment cancelled — back in Demo Posting for reassignment");
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to reassign tutor");
        }
    };

    const handleRestoreLead = async (row) => {
        setOpenMenu(null);
        try {
            await api.post(`/admin/pipeline/registrations/${row.id}/transition`, {
                to_stage: "leads",
                sub_status: "restored",
                reason: "Restored from Closed",
            });
            toast.success("Lead restored to Demo Leads");
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to restore lead");
        }
    };

    const handleCopyToLead = async (row) => {
        setOpenMenu(null);
        try {
            await api.post(`/admin/pipeline/registrations/${row.id}/transition`, {
                to_stage: "leads",
                sub_status: "ready_to_publish",
                reason: "Copied back to lead pool from posting",
            });
            toast.success("Moved back to Demo Leads");
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to copy to lead");
        }
    };

    const handleHidePost = async (row) => {
        setOpenMenu(null);
        try {
            // V34 — Hide Post is just an archive with reason='hidden' so the
            // posting disappears from the Demo Posting list. Operator can
            // unarchive from the Closed stage later.
            await api.delete(`/admin/pipeline/registrations/${row.id}`);
            toast.success("Posting hidden (moved to Closed)");
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to hide post");
        }
    };

    // V34.3 — Demo stage actions
    const handleKeepAndRepost = async (row) => {
        setOpenMenu(null);
        try {
            // Keep the active assignment intact, but move the lead back to the
            // demo pool so other tutors can also apply.
            await api.post(`/admin/pipeline/registrations/${row.id}/transition`, {
                to_stage: "demo",
                sub_status: "open_for_more_applicants",
                reason: "Kept tutor and re-opened to pool",
            });
            toast.success("Tutor kept & posting re-opened");
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to re-post");
        }
    };

    const handleDeleteAndRepost = async (row) => {
        setOpenMenu(null);
        try {
            await api.post(`/admin/leads/${row.id}/cancel-assignment`, { reason: "Deleted & reposted from Demos" });
            toast.success("Assignment cancelled — back in Demo Posting");
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to cancel assignment");
        }
    };

    const handleMarkDemoDone = async (row) => {
        setOpenMenu(null);
        try {
            await api.post(`/admin/pipeline/registrations/${row.id}/transition`, {
                to_stage: "completed_demo",
                sub_status: "awaiting_decision",
                reason: "Admin marked demo done",
            });
            toast.success("Marked demo as done");
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to mark demo done");
        }
    };

    const resetFilters = () => { setQ(""); setSource(""); };

    // V27 — Lead Pipeline reads PII + drives transitions; gated behind Super Mode.
    if (!sm?.active) {
        return (
            <div className="space-y-5" data-testid="lead-management-gate">
                <div>
                    <h2 className="font-heading text-3xl font-bold text-[#0F172A] tracking-tight">Leads</h2>
                    <p className="text-sm text-slate-500 mt-1">Manage and track your potential customers</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 via-white to-rose-50 border border-amber-200 rounded-2xl p-8 text-center max-w-2xl mx-auto">
                    <div className="w-14 h-14 rounded-2xl bg-amber-500/10 text-amber-600 flex items-center justify-center mx-auto">
                        <ShieldAlert className="w-7 h-7" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-[#0F172A] mt-4">Super Mode required</h3>
                    <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
                        The Lead Pipeline reads full parent contact details and lets you transition leads through demo, assignment and tuition stages. Click <span className="font-bold text-[#0F172A]">“Switch to Super Mode”</span> at the top of the screen and enter your MPIN to continue.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="lead-management-panel">
            {/* Header */}
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-3xl font-bold text-[#0F172A] tracking-tight">Leads</h2>
                    <p className="text-sm text-slate-500 mt-1">Manage and track your potential customers</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={refresh}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:border-slate-300 min-h-[44px]"
                        data-testid="leads-refresh-btn"
                    >
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh
                    </button>
                    <button
                        onClick={() => { setCreateSeed(null); setCreateOpen(true); }}
                        className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-bold text-sm rounded-xl shadow-sm hover:shadow-md transition active:scale-95"
                        data-testid="leads-add-new-btn"
                    >
                        <Plus className="w-4 h-4" /> Add New Lead
                    </button>
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="leads-kpi-strip">
                {kpis.map((k) => {
                    const Icon = k.Icon;
                    return (
                        <div key={k.label} data-testid={k.testid}
                            className={`bg-gradient-to-br ${k.accent} rounded-2xl border border-slate-200 p-4 transition hover:shadow-md`}>
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{k.label}</div>
                                    <div className="font-heading text-3xl font-extrabold text-[#0F172A] mt-1">{k.value}</div>
                                </div>
                                <div className={`w-10 h-10 rounded-xl ${k.iconTone} flex items-center justify-center shrink-0`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Stage tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto bg-white border border-slate-200 rounded-xl p-1.5" data-testid="leads-stage-tabs">
                {STAGE_TABS.map((s) => {
                    const active = stage === s.id;
                    const cnt = counts[s.id] ?? 0;
                    return (
                        <button
                            key={s.id}
                            data-testid={`stage-tab-${s.id}`}
                            onClick={() => setStage(s.id)}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-lg text-xs font-bold whitespace-nowrap transition ${
                                active ? "bg-[#0F172A] text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                            }`}
                        >
                            {s.label}
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ring-1 ${
                                active ? "bg-white/15 text-white ring-white/30" : "bg-slate-100 text-slate-700 ring-slate-200"
                            }`}>{cnt}</span>
                        </button>
                    );
                })}
            </div>

            {/* Lead / Posting / Demo Management section */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-end justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="font-heading text-xl font-bold text-[#0F172A]">
                            {isPostings ? "Requirements" : (isDemo ? "Demo Management" : (isTuition ? "Tuitions Management" : (isCompletedDemo ? "Demo Done Management" : (isClosed ? "Closed Leads Archive" : "Lead Management"))))}
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {isPostings ? "All requirements" : (isDemo ? "Filter and search through on-going demos" : (isTuition ? "Filter and search through your tuitions" : (isCompletedDemo ? "Capture parent feedback and start tuitions" : (isClosed ? "Restore or permanently remove lost leads" : "Filter and search through your leads"))))}
                        </p>
                    </div>
                    {(isPostings || isDemo || isTuition || isCompletedDemo || isClosed) && !loading && (
                        <div className="text-xs text-slate-500 italic" data-testid={`${stage}-count-indicator`}>
                            Currently showing <span className="font-bold text-[#0F172A] not-italic">{items.length}</span>
                            {isCompletedDemo ? " demos awaiting decision" : (isClosed ? " closed leads" : (isTuition ? " active tuitions" : (isDemo ? " on-going demos" : " postings")))}
                        </div>
                    )}
                </div>

                {/* Filter bar */}
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap" data-testid="leads-filter-bar">
                    <div className="flex items-center gap-2 flex-1 min-w-[260px] bg-slate-50 rounded-xl px-3 py-2 ring-1 ring-slate-200">
                        <Search className="w-4 h-4 text-slate-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by name, email, or phone…"
                            className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
                            data-testid="leads-search-input"
                        />
                    </div>
                    {!isPostings && !isDemo && !isTuition && !isCompletedDemo && !isClosed && (
                        <select
                            value={source}
                            onChange={(e) => setSource(e.target.value)}
                            className="px-3 py-2 min-h-[40px] rounded-xl border border-slate-200 text-sm bg-white text-slate-700"
                            data-testid="leads-source-filter"
                        >
                            <option value="">Select Source</option>
                            <option value="website">Website</option>
                            <option value="call">Call</option>
                            <option value="manual">Manual Entry</option>
                            <option value="referral">Referral</option>
                            <option value="walkin">Walk-in</option>
                        </select>
                    )}
                    {isTuition && (
                        <select
                            value={source}
                            onChange={(e) => setSource(e.target.value)}
                            className="px-3 py-2 min-h-[40px] rounded-xl border border-slate-200 text-sm bg-white text-slate-700"
                            data-testid="tuition-fee-status-filter"
                        >
                            <option value="">Fee Status</option>
                            <option value="paid">Paid</option>
                            <option value="due">Due</option>
                            <option value="overdue">Overdue</option>
                            <option value="partial">Partial</option>
                        </select>
                    )}
                    {isPostings && (
                        <select
                            value={source}
                            onChange={(e) => setSource(e.target.value)}
                            className="px-3 py-2 min-h-[40px] rounded-xl border border-slate-200 text-sm bg-white text-slate-700"
                            data-testid="postings-applicants-filter"
                        >
                            <option value="">Applicants</option>
                            <option value="0">No applicants yet</option>
                            <option value="1">1+ applied</option>
                            <option value="3">3+ applied</option>
                            <option value="5">5+ applied</option>
                        </select>
                    )}
                    {isDemo && (
                        <select
                            value={source}
                            onChange={(e) => setSource(e.target.value)}
                            className="px-3 py-2 min-h-[40px] rounded-xl border border-slate-200 text-sm bg-white text-slate-700"
                            data-testid="demo-status-filter"
                        >
                            <option value="">Select Status</option>
                            <option value="assigned">Assigned</option>
                            <option value="tutor_accepted">Accepted</option>
                            <option value="demo_scheduled">Scheduled</option>
                            <option value="demo_done">Demo Done</option>
                        </select>
                    )}
                    <button
                        onClick={resetFilters}
                        className="px-4 py-2 min-h-[40px] rounded-xl bg-[#0F172A] hover:bg-[#0F172A]/90 text-white text-xs font-bold uppercase tracking-wider"
                        data-testid="leads-reset-filters"
                    >
                        Reset
                    </button>
                    {isDemo && (
                        <button
                            onClick={() => { setStage("demo"); setSource(""); toast.info("Showing demo pool — tutor-rejected leads return here for reassignment"); }}
                            className="px-4 py-2 min-h-[40px] rounded-xl bg-[#0F172A] hover:bg-[#0F172A]/90 text-white text-xs font-bold uppercase tracking-wider"
                            data-testid="demo-rejected-by-tutor-btn"
                            title="Tutor-rejected demos return to the Demo Posting pool"
                        >
                            Rejected By Tutor
                        </button>
                    )}
                </div>

                {/* Table */}
                <div className="overflow-x-auto" data-testid={`leads-table-${stage}`}>
                    <table className="w-full text-sm">
                        <thead>
                            {!isPostings && !isDemo && !isTuition && !isCompletedDemo && !isClosed && (
                                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
                                    <th className="text-left font-semibold px-5 py-3">Parent Name</th>
                                    <th className="text-left font-semibold px-3 py-3">Grade / Subjects</th>
                                    <th className="text-left font-semibold px-3 py-3">Preference</th>
                                    <th className="text-left font-semibold px-3 py-3">Address</th>
                                    <th className="text-left font-semibold px-3 py-3">Created By</th>
                                    <th className="text-left font-semibold px-3 py-3">Source</th>
                                    <th className="text-left font-semibold px-3 py-3">Priority</th>
                                    <th className="text-left font-semibold px-3 py-3">Journal</th>
                                    <th className="text-right font-semibold px-5 py-3">Actions</th>
                                </tr>
                            )}
                            {isPostings && (
                                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
                                    <th className="text-left font-semibold px-5 py-3">Parent</th>
                                    <th className="text-left font-semibold px-3 py-3">Preference</th>
                                    <th className="text-left font-semibold px-3 py-3">Grade &amp; Subject</th>
                                    <th className="text-left font-semibold px-3 py-3">Address</th>
                                    <th className="text-left font-semibold px-3 py-3">Posted By</th>
                                    <th className="text-left font-semibold px-3 py-3">Fee</th>
                                    <th className="text-left font-semibold px-3 py-3">Status</th>
                                    <th className="text-left font-semibold px-3 py-3">Journal</th>
                                    <th className="text-right font-semibold px-5 py-3">Actions</th>
                                </tr>
                            )}
                            {isDemo && (
                                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
                                    <th className="text-left font-semibold px-5 py-3">Parent</th>
                                    <th className="text-left font-semibold px-3 py-3">Grade &amp; Subject</th>
                                    <th className="text-left font-semibold px-3 py-3">Address</th>
                                    <th className="text-left font-semibold px-3 py-3">Teacher</th>
                                    <th className="text-left font-semibold px-3 py-3">Fee</th>
                                    <th className="text-left font-semibold px-3 py-3">Assigned By</th>
                                    <th className="text-left font-semibold px-3 py-3">Status</th>
                                    <th className="text-left font-semibold px-3 py-3">Journal</th>
                                    <th className="text-right font-semibold px-5 py-3">Actions</th>
                                </tr>
                            )}
                            {isTuition && (
                                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
                                    <th className="text-left font-semibold px-5 py-3">Parent</th>
                                    <th className="text-left font-semibold px-3 py-3">Grade/Subject</th>
                                    <th className="text-left font-semibold px-3 py-3">Address</th>
                                    <th className="text-left font-semibold px-3 py-3">Teacher</th>
                                    <th className="text-left font-semibold px-3 py-3">Fee Details</th>
                                    <th className="text-left font-semibold px-3 py-3">Approved By</th>
                                    <th className="text-left font-semibold px-3 py-3">Dates</th>
                                    <th className="text-left font-semibold px-3 py-3">Journal</th>
                                    <th className="text-right font-semibold px-5 py-3">Actions</th>
                                </tr>
                            )}
                            {isCompletedDemo && (
                                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
                                    <th className="text-left font-semibold px-5 py-3">Parent</th>
                                    <th className="text-left font-semibold px-3 py-3">Grade/Subject</th>
                                    <th className="text-left font-semibold px-3 py-3">Address</th>
                                    <th className="text-left font-semibold px-3 py-3">Teacher</th>
                                    <th className="text-left font-semibold px-3 py-3">Demo Done At</th>
                                    <th className="text-left font-semibold px-3 py-3">Parent Feedback</th>
                                    <th className="text-left font-semibold px-3 py-3">Status</th>
                                    <th className="text-left font-semibold px-3 py-3">Journal</th>
                                    <th className="text-right font-semibold px-5 py-3">Actions</th>
                                </tr>
                            )}
                            {isClosed && (
                                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
                                    <th className="text-left font-semibold px-5 py-3">Parent</th>
                                    <th className="text-left font-semibold px-3 py-3">Grade/Subject</th>
                                    <th className="text-left font-semibold px-3 py-3">Last Tutor</th>
                                    <th className="text-left font-semibold px-3 py-3">Closed Reason</th>
                                    <th className="text-left font-semibold px-3 py-3">Closed By</th>
                                    <th className="text-left font-semibold px-3 py-3">Closed At</th>
                                    <th className="text-left font-semibold px-3 py-3">Journal</th>
                                    <th className="text-right font-semibold px-5 py-3">Actions</th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={9} className="text-center py-12 text-slate-500 text-sm">
                                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading {isPostings ? "postings" : (isDemo ? "demos" : (isTuition ? "tuitions" : "leads"))}…
                                </td></tr>
                            )}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={9} className="text-center py-12 text-slate-500 text-sm">
                                    {isPostings ? "No live postings in this stage." : (isDemo ? "No on-going demos right now." : (isTuition ? "No active tuitions yet." : "No leads in this stage."))}
                                    {stage === "leads" && (
                                        <button onClick={() => { setCreateSeed(null); setCreateOpen(true); }} className="ml-2 text-rose-600 font-semibold hover:underline">Create one</button>
                                    )}
                                </td></tr>
                            )}
                            {!loading && items.map((row) => {
                                if (isPostings) {
                                    return (
                                        <PostingRow
                                            key={row.id}
                                            row={row}
                                            isMenuOpen={openMenu === row.id}
                                            onMenuToggle={() => setOpenMenu(openMenu === row.id ? null : row.id)}
                                            onView={() => { setOpenMenu(null); setDetail(row); }}
                                            onAssignDemo={() => { setOpenMenu(null); setDetail(row); }}
                                            onCopyToLead={() => handleCopyToLead(row)}
                                            onHidePost={() => handleHidePost(row)}
                                            onDelete={() => { setOpenMenu(null); setConfirming({ id: row.id, name: row.name || "this posting" }); }}
                                        />
                                    );
                                }
                                if (isDemo) {
                                    return (
                                        <DemoRow
                                            key={row.id}
                                            row={row}
                                            isMenuOpen={openMenu === row.id}
                                            onMenuToggle={() => setOpenMenu(openMenu === row.id ? null : row.id)}
                                            onView={() => { setOpenMenu(null); setDetail(row); }}
                                            onCopyToLead={() => handleCopyToLead(row)}
                                            onKeepAndRepost={() => handleKeepAndRepost(row)}
                                            onDeleteAndRepost={() => handleDeleteAndRepost(row)}
                                            onMarkDemoDone={() => handleMarkDemoDone(row)}
                                            onDelete={() => { setOpenMenu(null); setConfirming({ id: row.id, name: row.name || "this demo" }); }}
                                        />
                                    );
                                }
                                if (isTuition) {
                                    return (
                                        <TuitionRow
                                            key={row.id}
                                            row={row}
                                            isMenuOpen={openMenu === row.id}
                                            onMenuToggle={() => setOpenMenu(openMenu === row.id ? null : row.id)}
                                            onView={() => { setOpenMenu(null); setDetail(row); }}
                                            onActivities={() => { setOpenMenu(null); setDetail(row); }}
                                            onEditLead={() => { setOpenMenu(null); setDetail(row); }}
                                            onCallLead={() => handleCallLead(row)}
                                            onDelete={() => { setOpenMenu(null); setConfirming({ id: row.id, name: row.name || "this tuition" }); }}
                                        />
                                    );
                                }
                                if (isCompletedDemo) {
                                    return (
                                        <CompletedDemoRow
                                            key={row.id}
                                            row={row}
                                            isMenuOpen={openMenu === row.id}
                                            onMenuToggle={() => setOpenMenu(openMenu === row.id ? null : row.id)}
                                            onView={() => { setOpenMenu(null); setDetail(row); }}
                                            onStartTuition={() => handleStartTuition(row)}
                                            onReassignTutor={() => handleReassignTutor(row)}
                                            onCloseAsLost={() => handleCloseAsLost(row)}
                                            onDelete={() => { setOpenMenu(null); setConfirming({ id: row.id, name: row.name || "this demo" }); }}
                                        />
                                    );
                                }
                                if (isClosed) {
                                    return (
                                        <ClosedRow
                                            key={row.id}
                                            row={row}
                                            isMenuOpen={openMenu === row.id}
                                            onMenuToggle={() => setOpenMenu(openMenu === row.id ? null : row.id)}
                                            onView={() => { setOpenMenu(null); setDetail(row); }}
                                            onRestore={() => handleRestoreLead(row)}
                                        />
                                    );
                                }
                                return (
                                    <LeadRow
                                        key={row.id}
                                        row={row}
                                        isMenuOpen={openMenu === row.id}
                                        onMenuToggle={() => setOpenMenu(openMenu === row.id ? null : row.id)}
                                        onView={() => { setOpenMenu(null); setDetail(row); }}
                                        onDuplicate={() => handleDuplicate(row)}
                                        onDelete={() => { setOpenMenu(null); setConfirming({ id: row.id, name: row.name || "this lead" }); }}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <CreateDemoLeadModal
                open={createOpen}
                seed={createSeed}
                onClose={() => { setCreateOpen(false); setCreateSeed(null); }}
                onCreated={(data) => { refresh(); setStage("leads"); setDetail(data.lead); }}
            />
            {detail && (
                <LeadDetailModal
                    kind="registrations"
                    item={detail}
                    onClose={() => { setDetail(null); refresh(); }}
                    onSaved={refresh}
                />
            )}
            {confirming && (
                <ConfirmDelete
                    name={confirming.name}
                    onCancel={() => setConfirming(null)}
                    onConfirm={() => handleDelete({ id: confirming.id })}
                />
            )}
        </div>
    );
}

function LeadRow({ row, isMenuOpen, onMenuToggle, onView, onDuplicate, onDelete }) {
    const menuRef = useRef(null);
    useEffect(() => {
        if (!isMenuOpen) return undefined;
        const onClickAway = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) onMenuToggle();
        };
        document.addEventListener("mousedown", onClickAway);
        return () => document.removeEventListener("mousedown", onClickAway);
    }, [isMenuOpen, onMenuToggle]);

    const grade = row.grade || "—";
    const subjects = (row.subjects || "").trim();
    const gradeSub = subjects ? `${grade} — ${subjects}` : grade;
    const mode = (row.mode || "").trim();
    const modeLabel = /online/i.test(mode) ? "Online" : (/home/i.test(mode) ? "Home Tuition" : (mode || "—"));
    const modeTone = /online/i.test(mode) ? "bg-orange-100 text-orange-700 ring-orange-200" : "bg-pink-100 text-pink-700 ring-pink-200";
    const addr = row.city ? `${row.city}${row.state ? ", " + row.state : ""}` : (row.address || "—");
    const createdBy = row.created_by || row.assigned_to || "Admin";
    const sourceRaw = (row.source || row.lead_source || row.callback_meta?.cta_source || (row.callback_meta ? "call" : "website") || "website").toLowerCase();
    const sourceLabel = sourceRaw === "callback_demo" ? "Call" : sourceRaw.charAt(0).toUpperCase() + sourceRaw.slice(1);
    const sourceTone = SOURCE_TONE[sourceRaw] || SOURCE_TONE.manual;
    const priority = (row.priority || "normal").toLowerCase();
    const priorityTone = PRIORITY_TONE[priority] || PRIORITY_TONE.normal;
    const ref = row.referred_by || row.ref_by;

    return (
        <tr className="border-b border-slate-50 hover:bg-slate-50/60 transition" data-testid={`lead-row-${row.id}`}>
            <td className="px-5 py-4 align-top">
                <div className="font-semibold text-[#0F172A]">{row.parent_name || row.name || "—"}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">INS: Anand Tutorials</div>
                {row.phone && (
                    <div className="text-[12px] text-slate-600 mt-1 flex items-center gap-1">
                        <PhoneIcon className="w-3 h-3" /> {row.phone}
                    </div>
                )}
                <div className="mt-1">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 font-semibold">
                        Ref. By: {ref || "N/A"}
                    </span>
                </div>
            </td>
            <td className="px-3 py-4 align-top text-[#0F172A]">{gradeSub}</td>
            <td className="px-3 py-4 align-top">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${modeTone}`}>{modeLabel}</span>
                <div className="text-[11px] text-slate-500 mt-1">Any Tutor</div>
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm">{addr}</div>
                {row.city && (
                    <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {row.city}
                    </div>
                )}
            </td>
            <td className="px-3 py-4 align-top">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-100">{createdBy}</span>
                <div className="text-[11px] text-slate-500 mt-1">{fmtDateTime(row.created_at)}</div>
            </td>
            <td className="px-3 py-4 align-top">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${sourceTone}`}>{sourceLabel}</span>
            </td>
            <td className="px-3 py-4 align-top">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${priorityTone}`}>{priority.charAt(0).toUpperCase() + priority.slice(1)}</span>
            </td>
            <td className="px-3 py-4 align-top">
                <button onClick={onView} className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-rose-600 font-semibold" data-testid={`lead-journal-${row.id}`}>
                    <FileText className="w-3.5 h-3.5" /> Open
                </button>
            </td>
            <td className="px-5 py-4 align-top text-right relative">
                <button
                    onClick={onMenuToggle}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500"
                    data-testid={`lead-actions-${row.id}`}
                    aria-label="Open actions menu"
                >
                    <MoreHorizontal className="w-4 h-4" />
                </button>
                {isMenuOpen && (
                    <div
                        ref={menuRef}
                        className="absolute right-4 top-12 z-30 min-w-[200px] bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 text-left"
                        data-testid={`lead-actions-menu-${row.id}`}
                    >
                        <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Actions</div>
                        <button onClick={onView} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2" data-testid={`lead-view-${row.id}`}>
                            <Eye className="w-4 h-4 text-slate-500" /> View Details
                        </button>
                        <button onClick={onView} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2" data-testid={`lead-requirement-${row.id}`}>
                            <FileText className="w-4 h-4 text-violet-500" /> Make Requirement
                        </button>
                        <button onClick={onDuplicate} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2" data-testid={`lead-duplicate-${row.id}`}>
                            <CopyIcon className="w-4 h-4 text-emerald-500" /> Duplicate
                        </button>
                        <div className="my-1 border-t border-slate-100" />
                        <button onClick={onDelete} className="w-full px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 inline-flex items-center gap-2" data-testid={`lead-delete-${row.id}`}>
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    </div>
                )}
            </td>
        </tr>
    );
}

function PostingRow({ row, isMenuOpen, onMenuToggle, onView, onAssignDemo, onCopyToLead, onHidePost, onDelete }) {
    const menuRef = useRef(null);
    useEffect(() => {
        if (!isMenuOpen) return undefined;
        const onClickAway = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) onMenuToggle();
        };
        document.addEventListener("mousedown", onClickAway);
        return () => document.removeEventListener("mousedown", onClickAway);
    }, [isMenuOpen, onMenuToggle]);

    const grade = row.grade || "—";
    const subjects = (row.subjects || "").trim();
    const gradeSub = subjects ? `${grade} — ${subjects}` : grade;
    const cadence = row.frequency || row.weekly_pattern || "Weekly~5 Days";
    const duration = row.session_duration || "60 min";
    const mode = (row.mode || "").trim();
    const modeLabel = /online/i.test(mode) ? "Online" : (/home/i.test(mode) ? "Home Tuition" : (mode || "—"));
    const modeTone = /online/i.test(mode) ? "bg-orange-100 text-orange-700 ring-orange-200" : "bg-pink-100 text-pink-700 ring-pink-200";
    const preference = row.tutor_gender_preference || row.gender_preference || "Any";
    const addr = row.address || row.address_area || row.city || "—";
    const postedBy = row.posted_by || row.created_by || row.assigned_to || "Admin";
    const tuitionFee = row.quoted_fee || row.tuition_fee || 0;
    const tutorFee = row.tutor_in_hand_fee || row.tutor_fee || 0;
    const applicants = row.applicants_count ?? 0;
    const visibility = (row.visibility || (row.archived ? "hidden" : "live")).toLowerCase();
    const isLive = visibility === "live" || visibility === "active";
    const ref = row.referred_by || row.ref_by;
    const postingShortId = (row.id || "").slice(-4).toUpperCase() || "----";

    return (
        <tr className="border-b border-slate-50 hover:bg-emerald-50/30 transition" data-testid={`posting-row-${row.id}`}>
            <td className="px-5 py-4 align-top">
                <div className="font-semibold text-[#0F172A]">{row.parent_name || row.name || "—"}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">INS: Anand Tutorials</div>
                {row.phone && (
                    <div className="text-[12px] text-slate-600 mt-1 flex items-center gap-1">
                        <PhoneIcon className="w-3 h-3" /> {row.phone}
                    </div>
                )}
                <div className="mt-1">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 font-semibold">
                        Ref. By: {ref || "N/A"}
                    </span>
                </div>
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm font-medium">{preference}</div>
                <div className="mt-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${modeTone}`}>{modeLabel}</span>
                </div>
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm">{gradeSub}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{cadence} | {duration}</div>
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm truncate max-w-[180px]">{addr}</div>
                {row.city && (
                    <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {row.city}
                    </div>
                )}
            </td>
            <td className="px-3 py-4 align-top">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-100">{postedBy}</span>
                <div className="text-[11px] text-slate-500 mt-1">{fmtDateTime(row.created_at)}</div>
            </td>
            <td className="px-3 py-4 align-top text-[12px] text-slate-700">
                <div><span className="font-semibold text-[#0F172A]">Tuition:</span> {tuitionFee || 0} Rs.</div>
                <div className="mt-0.5"><span className="font-semibold text-[#0F172A]">Tutor:</span> {tutorFee || 0} Rs.</div>
            </td>
            <td className="px-3 py-4 align-top">
                <div className="inline-flex flex-col items-center gap-1">
                    <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ring-1 ${
                        isLive ? "bg-emerald-500 text-white ring-emerald-600" : "bg-slate-400 text-white ring-slate-500"
                    }`}>{isLive ? "Live" : "Hidden"}</span>
                    <span className={`text-[10px] font-bold w-6 h-6 rounded-full inline-flex items-center justify-center ring-1 ${
                        applicants > 0 ? "bg-emerald-100 text-emerald-700 ring-emerald-200" : "bg-slate-100 text-slate-500 ring-slate-200"
                    }`} data-testid={`posting-applicants-${row.id}`}>
                        {applicants}
                    </span>
                </div>
            </td>
            <td className="px-3 py-4 align-top">
                <button onClick={onView} className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-rose-600 font-semibold" data-testid={`posting-journal-${row.id}`}>
                    <FileText className="w-3.5 h-3.5" /> Open
                </button>
            </td>
            <td className="px-5 py-4 align-top text-right relative">
                <button
                    onClick={onMenuToggle}
                    className={`w-8 h-8 inline-flex items-center justify-center rounded-lg transition ${
                        isMenuOpen ? "bg-rose-500 text-white" : "hover:bg-slate-100 text-slate-500"
                    }`}
                    data-testid={`posting-actions-${row.id}`}
                    aria-label="Open posting actions menu"
                >
                    <MoreHorizontal className="w-4 h-4" />
                </button>
                {isMenuOpen && (
                    <div
                        ref={menuRef}
                        className="absolute right-4 top-12 z-30 min-w-[220px] bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 text-left"
                        data-testid={`posting-actions-menu-${row.id}`}
                    >
                        <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            Actions <span className="text-slate-500 normal-case">POS - {postingShortId}</span>
                        </div>
                        <button onClick={onView} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2" data-testid={`posting-view-${row.id}`}>
                            <Eye className="w-4 h-4 text-slate-500" /> View Details
                        </button>
                        <button onClick={onAssignDemo} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 inline-flex items-center gap-2" data-testid={`posting-assign-${row.id}`}>
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Assign Demo
                        </button>
                        <button onClick={onCopyToLead} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 inline-flex items-center gap-2" data-testid={`posting-copy-to-lead-${row.id}`}>
                            <CopyIcon className="w-4 h-4 text-blue-500" /> Copy To Lead
                        </button>
                        <button onClick={onHidePost} className="w-full px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 inline-flex items-center gap-2" data-testid={`posting-hide-${row.id}`}>
                            <EyeOff className="w-4 h-4" /> Hide Post
                        </button>
                        <div className="my-1 border-t border-slate-100" />
                        <button onClick={onDelete} className="w-full px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 inline-flex items-center gap-2" data-testid={`posting-delete-${row.id}`}>
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    </div>
                )}
            </td>
        </tr>
    );
}

function DemoRow({ row, isMenuOpen, onMenuToggle, onView, onCopyToLead, onKeepAndRepost, onDeleteAndRepost, onMarkDemoDone, onDelete }) {
    const menuRef = useRef(null);
    useEffect(() => {
        if (!isMenuOpen) return undefined;
        const onClickAway = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) onMenuToggle();
        };
        document.addEventListener("mousedown", onClickAway);
        return () => document.removeEventListener("mousedown", onClickAway);
    }, [isMenuOpen, onMenuToggle]);

    const grade = row.grade || "—";
    const subjects = (row.subjects || "").trim();
    const gradeSub = subjects ? `${grade} — ${subjects}` : grade;
    const cadence = row.frequency || row.weekly_pattern || "Weekly~5 Days";
    const duration = row.session_duration || "60 min";
    const mode = (row.mode || "").trim();
    const modeLabel = /online/i.test(mode) ? "Online" : (/home/i.test(mode) ? "Home Tuition" : (mode || "—"));
    const modeTone = /online/i.test(mode) ? "bg-orange-100 text-orange-700 ring-orange-200" : "bg-pink-100 text-pink-700 ring-pink-200";
    const addr = row.address || row.address_area || row.city || "—";
    const tutorName = row.assigned_tutor_name || row.tutor_name || "Awaiting tutor";
    const tutorPhone = row.assigned_tutor_phone || row.tutor_phone || "";
    const assignedBy = row.assigned_by || row.last_actor || "Admin";
    const assignedAt = row.assigned_at || row.updated_at || row.created_at;
    const tuitionFee = row.quoted_fee || row.tuition_fee || 0;
    const tutorFee = row.tutor_in_hand_fee || row.tutor_fee || 0;
    const ref = row.referred_by || row.ref_by;
    const demoShortId = (row.id || "").slice(-4).toUpperCase() || "----";

    // Status pill derived from pipeline_sub_status / active assignment status.
    const sub = (row.pipeline_sub_status || "assigned").toLowerCase();
    const statusMap = {
        assigned: { label: "Assigned", tone: "bg-blue-100 text-blue-800 ring-blue-200" },
        tutor_accepted: { label: "Accepted", tone: "bg-amber-100 text-amber-800 ring-amber-200" },
        demo_scheduled: { label: "Scheduled", tone: "bg-violet-100 text-violet-800 ring-violet-200" },
        demo_done: { label: "Demo Done", tone: "bg-emerald-100 text-emerald-800 ring-emerald-200" },
        tutor_rejected: { label: "Rejected by Tutor", tone: "bg-rose-100 text-rose-800 ring-rose-200" },
        demo_cancelled: { label: "Cancelled", tone: "bg-slate-100 text-slate-700 ring-slate-200" },
    };
    const status = statusMap[sub] || { label: sub.replace(/_/g, " "), tone: "bg-slate-100 text-slate-700 ring-slate-200" };

    return (
        <tr className="border-b border-slate-50 hover:bg-slate-50/60 transition" data-testid={`demo-row-${row.id}`}>
            <td className="px-5 py-4 align-top">
                <div className="font-semibold text-[#0F172A]">{row.parent_name || row.name || "—"}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">INS: Anand Tutorials</div>
                {row.phone && (
                    <div className="text-[12px] text-slate-600 mt-1 flex items-center gap-1">
                        <PhoneIcon className="w-3 h-3" /> {row.phone}
                    </div>
                )}
                <div className="mt-1">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 font-semibold">
                        Ref. By: {ref || "N/A"}
                    </span>
                </div>
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm">{gradeSub}</div>
                <div className="mt-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${modeTone}`}>{modeLabel}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{cadence} | {duration}</div>
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm truncate max-w-[170px]">{addr}</div>
                {row.city && (
                    <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {row.city}
                    </div>
                )}
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm font-medium flex items-center gap-1">
                    <GraduationCap className="w-3.5 h-3.5 text-slate-400" /> {tutorName}
                </div>
                {tutorPhone && (
                    <div className="text-[12px] text-slate-600 mt-1 flex items-center gap-1">
                        <PhoneIcon className="w-3 h-3" /> {tutorPhone}
                    </div>
                )}
            </td>
            <td className="px-3 py-4 align-top text-[12px] text-slate-700">
                <div><span className="font-semibold text-[#0F172A]">Tuition:</span> {tuitionFee || 0} Rs.</div>
                <div className="mt-0.5"><span className="font-semibold text-[#0F172A]">Tutor:</span> {tutorFee || 0} Rs.</div>
            </td>
            <td className="px-3 py-4 align-top">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-100">{assignedBy}</span>
                <div className="text-[11px] text-slate-500 mt-1">{fmtDateTime(assignedAt)}</div>
            </td>
            <td className="px-3 py-4 align-top">
                <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ring-1 ${status.tone}`} data-testid={`demo-status-${row.id}`}>
                    {status.label}
                </span>
            </td>
            <td className="px-3 py-4 align-top">
                <button onClick={onView} className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-rose-600 font-semibold" data-testid={`demo-journal-${row.id}`}>
                    <FileText className="w-3.5 h-3.5" /> Open
                </button>
            </td>
            <td className="px-5 py-4 align-top text-right relative">
                <button
                    onClick={onMenuToggle}
                    className={`w-8 h-8 inline-flex items-center justify-center rounded-lg transition ${
                        isMenuOpen ? "bg-rose-500 text-white" : "hover:bg-slate-100 text-slate-500"
                    }`}
                    data-testid={`demo-actions-${row.id}`}
                    aria-label="Open demo actions menu"
                >
                    <MoreHorizontal className="w-4 h-4" />
                </button>
                {isMenuOpen && (
                    <div
                        ref={menuRef}
                        className="absolute right-4 top-12 z-30 min-w-[230px] bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 text-left"
                        data-testid={`demo-actions-menu-${row.id}`}
                    >
                        <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            Actions <span className="text-slate-500 normal-case">demo - {demoShortId}</span>
                        </div>
                        <button onClick={onView} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2" data-testid={`demo-view-${row.id}`}>
                            <Eye className="w-4 h-4 text-slate-500" /> View Details
                        </button>
                        <button onClick={onCopyToLead} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 inline-flex items-center gap-2" data-testid={`demo-copy-to-lead-${row.id}`}>
                            <CopyIcon className="w-4 h-4 text-blue-500" /> Copy To Lead
                        </button>
                        <button onClick={onKeepAndRepost} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-violet-50 inline-flex items-center gap-2" data-testid={`demo-keep-repost-${row.id}`}>
                            <RotateCcw className="w-4 h-4 text-violet-500" /> Keep and Re-post
                        </button>
                        <button onClick={onDeleteAndRepost} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-orange-50 inline-flex items-center gap-2" data-testid={`demo-delete-repost-${row.id}`}>
                            <RefreshCw className="w-4 h-4 text-orange-500" /> Delete and Re-post
                        </button>
                        <button onClick={onMarkDemoDone} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 inline-flex items-center gap-2" data-testid={`demo-mark-done-${row.id}`}>
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Mark Demo Done
                        </button>
                        <div className="my-1 border-t border-slate-100" />
                        <button onClick={onDelete} className="w-full px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 inline-flex items-center gap-2" data-testid={`demo-delete-${row.id}`}>
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    </div>
                )}
            </td>
        </tr>
    );
}

function TuitionRow({ row, isMenuOpen, onMenuToggle, onView, onActivities, onEditLead, onCallLead, onDelete }) {
    const menuRef = useRef(null);
    useEffect(() => {
        if (!isMenuOpen) return undefined;
        const onClickAway = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) onMenuToggle();
        };
        document.addEventListener("mousedown", onClickAway);
        return () => document.removeEventListener("mousedown", onClickAway);
    }, [isMenuOpen, onMenuToggle]);

    const grade = row.grade || "—";
    const subjects = (row.subjects || "").trim();
    const gradeSub = subjects ? `${grade} — ${subjects}` : grade;
    const mode = (row.mode || "").trim();
    const modeLabel = /online/i.test(mode) ? "Online" : (/home/i.test(mode) ? "Home Tuition" : (mode || "—"));
    const modeTone = /online/i.test(mode) ? "bg-orange-100 text-orange-700 ring-orange-200" : "bg-pink-100 text-pink-700 ring-pink-200";
    const addr = row.city ? `, ${row.city}` : (row.address || "—");
    const tutorName = row.assigned_tutor_name || row.tutor_name || "—";
    const tutorPhone = row.assigned_tutor_phone || row.tutor_phone || "";
    const approvedBy = row.approved_by || row.last_actor || "Admin";
    const approvedAt = row.approved_at || row.tuition_started_at || row.updated_at || row.created_at;
    const tuitionFee = row.quoted_fee || row.tuition_fee || 0;
    const tutorFee = row.tutor_in_hand_fee || row.tutor_fee || 0;
    const ref = row.referred_by || row.ref_by;
    const tuitionShortId = (row.id || "").slice(-4).toUpperCase() || "----";

    // Dates — Start Date (when tuition was activated) + Collection Day-of-Month
    // (defaults to the day of approval).
    const fmtShortDate = (iso) => {
        if (!iso) return "—";
        try {
            const d = new Date(iso);
            const day = d.getDate();
            const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()];
            const yr = String(d.getFullYear()).slice(-2);
            return `${day} ${month} ${yr}`;
        } catch { return iso; }
    };
    const startDate = row.tuition_started_at || row.start_date || approvedAt;
    const collectionDay = row.collection_day || (startDate ? new Date(startDate).getDate() : null);

    return (
        <tr className="border-b border-slate-50 hover:bg-emerald-50/30 transition" data-testid={`tuition-row-${row.id}`}>
            <td className="px-5 py-4 align-top">
                <div className="font-semibold text-[#0F172A]">{row.parent_name || row.name || "—"}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">INS: Anand Tutorials</div>
                {row.phone && (
                    <div className="text-[12px] text-slate-600 mt-1 flex items-center gap-1">
                        <PhoneIcon className="w-3 h-3" /> {row.phone}
                    </div>
                )}
                <div className="mt-1">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 font-semibold">
                        Ref. By: {ref || "N/A"}
                    </span>
                </div>
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm">{gradeSub}</div>
                <div className="mt-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${modeTone}`}>{modeLabel}</span>
                </div>
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm truncate max-w-[160px]">{addr}</div>
                {row.city && (
                    <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {row.city}
                    </div>
                )}
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm font-medium flex items-center gap-1">
                    <GraduationCap className="w-3.5 h-3.5 text-slate-400" /> {tutorName}
                </div>
                {tutorPhone && (
                    <div className="text-[12px] text-slate-600 mt-1 flex items-center gap-1">
                        <PhoneIcon className="w-3 h-3" /> {tutorPhone}
                    </div>
                )}
            </td>
            <td className="px-3 py-4 align-top text-[12px] text-slate-700">
                <div><span className="font-semibold text-[#0F172A]">Tuition:</span> {tuitionFee || 0}</div>
                <div className="mt-0.5"><span className="font-semibold text-[#0F172A]">Tutor:</span> {tutorFee || 0}</div>
            </td>
            <td className="px-3 py-4 align-top">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-100">{approvedBy}</span>
                <div className="text-[11px] text-slate-500 mt-1">{fmtDateTime(approvedAt)}</div>
            </td>
            <td className="px-3 py-4 align-top text-[12px] text-slate-700">
                <div><span className="font-semibold text-[#0F172A]">St. Date:</span> {fmtShortDate(startDate)}</div>
                <div className="mt-0.5"><span className="font-semibold text-[#0F172A]">Coll. Date:</span> {collectionDay ?? "—"}</div>
            </td>
            <td className="px-3 py-4 align-top">
                <button onClick={onView} className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-rose-600 font-semibold" data-testid={`tuition-journal-${row.id}`}>
                    <FileText className="w-3.5 h-3.5" /> Open
                </button>
            </td>
            <td className="px-5 py-4 align-top text-right relative">
                <button
                    onClick={onMenuToggle}
                    className={`w-8 h-8 inline-flex items-center justify-center rounded-lg transition ${
                        isMenuOpen ? "bg-rose-500 text-white" : "hover:bg-slate-100 text-slate-500"
                    }`}
                    data-testid={`tuition-actions-${row.id}`}
                    aria-label="Open tuition actions menu"
                >
                    <MoreHorizontal className="w-4 h-4" />
                </button>
                {isMenuOpen && (
                    <div
                        ref={menuRef}
                        className="absolute right-4 top-12 z-30 min-w-[210px] bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 text-left"
                        data-testid={`tuition-actions-menu-${row.id}`}
                    >
                        <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Actions <span className="text-slate-500 normal-case">T - {tuitionShortId}</span></div>
                        <button onClick={onView} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2" data-testid={`tuition-view-${row.id}`}>
                            <Eye className="w-4 h-4 text-slate-500" /> View Details
                        </button>
                        <button onClick={onActivities} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-violet-50 inline-flex items-center gap-2" data-testid={`tuition-activities-${row.id}`}>
                            <Activity className="w-4 h-4 text-violet-500" /> Activities
                        </button>
                        <button onClick={onEditLead} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-amber-50 inline-flex items-center gap-2" data-testid={`tuition-edit-${row.id}`}>
                            <Pencil className="w-4 h-4 text-amber-500" /> Edit Lead
                        </button>
                        <button onClick={onCallLead} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 inline-flex items-center gap-2" data-testid={`tuition-call-${row.id}`}>
                            <PhoneIcon className="w-4 h-4 text-emerald-600" /> Call Lead
                        </button>
                        <div className="my-1 border-t border-slate-100" />
                        <button onClick={onDelete} className="w-full px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 inline-flex items-center gap-2" data-testid={`tuition-delete-${row.id}`}>
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    </div>
                )}
            </td>
        </tr>
    );
}

function CompletedDemoRow({ row, isMenuOpen, onMenuToggle, onView, onStartTuition, onReassignTutor, onCloseAsLost, onDelete }) {
    const menuRef = useRef(null);
    useEffect(() => {
        if (!isMenuOpen) return undefined;
        const onClickAway = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onMenuToggle(); };
        document.addEventListener("mousedown", onClickAway);
        return () => document.removeEventListener("mousedown", onClickAway);
    }, [isMenuOpen, onMenuToggle]);

    const grade = row.grade || "—";
    const subjects = (row.subjects || "").trim();
    const gradeSub = subjects ? `${grade} — ${subjects}` : grade;
    const mode = (row.mode || "").trim();
    const modeLabel = /online/i.test(mode) ? "Online" : (/home/i.test(mode) ? "Home Tuition" : (mode || "—"));
    const modeTone = /online/i.test(mode) ? "bg-orange-100 text-orange-700 ring-orange-200" : "bg-pink-100 text-pink-700 ring-pink-200";
    const addr = row.address || row.address_area || row.city || "—";
    const tutorName = row.assigned_tutor_name || row.tutor_name || "—";
    const demoDoneAt = row.demo_done_at || row.updated_at;
    const feedback = row.parent_feedback?.decision || row.parent_feedback_decision || "pending";
    const feedbackMap = {
        approve: { label: "Approve", tone: "bg-emerald-100 text-emerald-800 ring-emerald-200" },
        reassign: { label: "Reassign", tone: "bg-amber-100 text-amber-800 ring-amber-200" },
        reject: { label: "Not Interested", tone: "bg-rose-100 text-rose-800 ring-rose-200" },
        pending: { label: "Awaiting", tone: "bg-slate-100 text-slate-700 ring-slate-200" },
    };
    const fb = feedbackMap[feedback] || feedbackMap.pending;
    const sub = (row.pipeline_sub_status || "awaiting_decision").toLowerCase();
    const statusLabel = sub.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const ref = row.referred_by || row.ref_by;
    const shortId = (row.id || "").slice(-4).toUpperCase() || "----";

    return (
        <tr className="border-b border-slate-50 hover:bg-amber-50/30 transition" data-testid={`completed-demo-row-${row.id}`}>
            <td className="px-5 py-4 align-top">
                <div className="font-semibold text-[#0F172A]">{row.parent_name || row.name || "—"}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">INS: Anand Tutorials</div>
                {row.phone && <div className="text-[12px] text-slate-600 mt-1 flex items-center gap-1"><PhoneIcon className="w-3 h-3" /> {row.phone}</div>}
                <div className="mt-1"><span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 font-semibold">Ref. By: {ref || "N/A"}</span></div>
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm">{gradeSub}</div>
                <div className="mt-1"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${modeTone}`}>{modeLabel}</span></div>
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm truncate max-w-[170px]">{addr}</div>
                {row.city && <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {row.city}</div>}
            </td>
            <td className="px-3 py-4 align-top">
                <div className="text-[#0F172A] text-sm font-medium flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5 text-slate-400" /> {tutorName}</div>
            </td>
            <td className="px-3 py-4 align-top text-[12px] text-slate-700">{fmtDateTime(demoDoneAt)}</td>
            <td className="px-3 py-4 align-top">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${fb.tone}`} data-testid={`completed-demo-feedback-${row.id}`}>{fb.label}</span>
            </td>
            <td className="px-3 py-4 align-top">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 ring-1 ring-violet-200">{statusLabel}</span>
            </td>
            <td className="px-3 py-4 align-top">
                <button onClick={onView} className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-rose-600 font-semibold" data-testid={`completed-demo-journal-${row.id}`}>
                    <FileText className="w-3.5 h-3.5" /> Open
                </button>
            </td>
            <td className="px-5 py-4 align-top text-right relative">
                <button onClick={onMenuToggle} className={`w-8 h-8 inline-flex items-center justify-center rounded-lg transition ${isMenuOpen ? "bg-rose-500 text-white" : "hover:bg-slate-100 text-slate-500"}`} data-testid={`completed-demo-actions-${row.id}`} aria-label="Open actions menu">
                    <MoreHorizontal className="w-4 h-4" />
                </button>
                {isMenuOpen && (
                    <div ref={menuRef} className="absolute right-4 top-12 z-30 min-w-[220px] bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 text-left" data-testid={`completed-demo-actions-menu-${row.id}`}>
                        <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Actions <span className="text-slate-500 normal-case">DD - {shortId}</span></div>
                        <button onClick={onView} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2" data-testid={`completed-demo-view-${row.id}`}>
                            <Eye className="w-4 h-4 text-slate-500" /> View Details
                        </button>
                        <button onClick={onStartTuition} className="w-full px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-2 font-semibold" data-testid={`completed-demo-start-tuition-${row.id}`}>
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Start Tuition
                        </button>
                        <button onClick={onReassignTutor} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-violet-50 inline-flex items-center gap-2" data-testid={`completed-demo-reassign-${row.id}`}>
                            <RotateCcw className="w-4 h-4 text-violet-500" /> Reassign Tutor
                        </button>
                        <button onClick={onCloseAsLost} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-amber-50 inline-flex items-center gap-2" data-testid={`completed-demo-close-lost-${row.id}`}>
                            <XCircle className="w-4 h-4 text-amber-500" /> Close as Lost
                        </button>
                        <div className="my-1 border-t border-slate-100" />
                        <button onClick={onDelete} className="w-full px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 inline-flex items-center gap-2" data-testid={`completed-demo-delete-${row.id}`}>
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    </div>
                )}
            </td>
        </tr>
    );
}

function ClosedRow({ row, isMenuOpen, onMenuToggle, onView, onRestore }) {
    const menuRef = useRef(null);
    useEffect(() => {
        if (!isMenuOpen) return undefined;
        const onClickAway = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onMenuToggle(); };
        document.addEventListener("mousedown", onClickAway);
        return () => document.removeEventListener("mousedown", onClickAway);
    }, [isMenuOpen, onMenuToggle]);

    const grade = row.grade || "—";
    const subjects = (row.subjects || "").trim();
    const gradeSub = subjects ? `${grade} — ${subjects}` : grade;
    const lastTutor = row.assigned_tutor_name || row.tutor_name || "—";
    const closeReason = row.archived_reason || row.last_close_reason || row.pipeline_sub_status || "—";
    const closedBy = row.archived_by || row.last_actor || "Admin";
    const closedAt = row.archived_at || row.updated_at;
    const ref = row.referred_by || row.ref_by;
    const shortId = (row.id || "").slice(-4).toUpperCase() || "----";
    const subToneMap = {
        lost: "bg-rose-100 text-rose-800 ring-rose-200",
        completed: "bg-emerald-100 text-emerald-800 ring-emerald-200",
        cancelled: "bg-slate-100 text-slate-700 ring-slate-200",
    };
    const reasonTone = subToneMap[(closeReason || "").toLowerCase()] || "bg-slate-100 text-slate-700 ring-slate-200";

    return (
        <tr className="border-b border-slate-50 hover:bg-slate-50/60 transition opacity-90" data-testid={`closed-row-${row.id}`}>
            <td className="px-5 py-4 align-top">
                <div className="font-semibold text-[#0F172A]">{row.parent_name || row.name || "—"}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">INS: Anand Tutorials</div>
                {row.phone && <div className="text-[12px] text-slate-600 mt-1 flex items-center gap-1"><PhoneIcon className="w-3 h-3" /> {row.phone}</div>}
                <div className="mt-1"><span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 font-semibold">Ref. By: {ref || "N/A"}</span></div>
            </td>
            <td className="px-3 py-4 align-top text-[#0F172A] text-sm">{gradeSub}</td>
            <td className="px-3 py-4 align-top text-[#0F172A] text-sm">
                <div className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5 text-slate-400" /> {lastTutor}</div>
            </td>
            <td className="px-3 py-4 align-top">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${reasonTone}`} data-testid={`closed-reason-${row.id}`}>
                    {String(closeReason || "—").replace(/_/g, " ")}
                </span>
            </td>
            <td className="px-3 py-4 align-top">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-100">{closedBy}</span>
            </td>
            <td className="px-3 py-4 align-top text-[11px] text-slate-500">{fmtDateTime(closedAt)}</td>
            <td className="px-3 py-4 align-top">
                <button onClick={onView} className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-rose-600 font-semibold" data-testid={`closed-journal-${row.id}`}>
                    <FileText className="w-3.5 h-3.5" /> Open
                </button>
            </td>
            <td className="px-5 py-4 align-top text-right relative">
                <button onClick={onMenuToggle} className={`w-8 h-8 inline-flex items-center justify-center rounded-lg transition ${isMenuOpen ? "bg-rose-500 text-white" : "hover:bg-slate-100 text-slate-500"}`} data-testid={`closed-actions-${row.id}`} aria-label="Open actions menu">
                    <MoreHorizontal className="w-4 h-4" />
                </button>
                {isMenuOpen && (
                    <div ref={menuRef} className="absolute right-4 top-12 z-30 min-w-[210px] bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 text-left" data-testid={`closed-actions-menu-${row.id}`}>
                        <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Actions <span className="text-slate-500 normal-case">C - {shortId}</span></div>
                        <button onClick={onView} className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2" data-testid={`closed-view-${row.id}`}>
                            <Eye className="w-4 h-4 text-slate-500" /> View Details
                        </button>
                        <button onClick={onRestore} className="w-full px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-2 font-semibold" data-testid={`closed-restore-${row.id}`}>
                            <Undo2 className="w-4 h-4 text-emerald-600" /> Restore to Demo Leads
                        </button>
                    </div>
                )}
            </td>
        </tr>
    );
}

function ConfirmDelete({ name, onCancel, onConfirm }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="lead-delete-confirm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                        <Trash2 className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-heading text-lg font-bold text-[#0F172A]">Archive this lead?</h3>
                        <p className="text-sm text-slate-500 mt-1">
                            <span className="font-semibold text-slate-700">{name}</span> will be moved to the Closed stage. You can restore it later if needed.
                        </p>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onCancel} className="px-4 py-2 min-h-[40px] text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50" data-testid="lead-delete-cancel">
                        Cancel
                    </button>
                    <button onClick={onConfirm} className="px-4 py-2 min-h-[40px] text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-lg" data-testid="lead-delete-confirm-btn">
                        Archive Lead
                    </button>
                </div>
            </div>
        </div>
    );
}
